import { UiDomUtils } from '@dreamcommerce/utilities';
import { MenuKeyboardNavigationUtils } from '@frontstoreRwd/modules/rwd_menu/menu_navigation/menu_keyboard_navigation_utils';
import { IMenuKeyboardNavigation, TCloseAndNavigateOptions, THandleArrowNavigationOptions } from '@frontstoreRwd/modules/rwd_menu/menu_navigation/menu_keyboard_navigation_types';
import { IMenuKeyboardNavigationUtils } from '@frontstoreRwd/modules/rwd_menu/menu_navigation/menu_keyboard_navigation_utils_types';
import { MENU_CSS_CLASSES } from '@frontstoreRwd/modules/rwd_menu/menu_navigation/menu_keyboard_navigation_constants';

export class MenuKeyboardNavigation implements IMenuKeyboardNavigation {
    public navigationUtils: IMenuKeyboardNavigationUtils;
    private _$menuBar: HTMLElement;

    constructor($menuBar: HTMLElement) {
        this._$menuBar = $menuBar;
        this.navigationUtils = new MenuKeyboardNavigationUtils(this._$menuBar);

        this._addNavigationEventListeners();
    }

    private _addNavigationEventListeners(): void {
        this.navigationUtils.$topLevelItemsWithSubmenu.forEach(($menuItem) => {
            $menuItem.addEventListener('keydown', this._handleSubMenuKeyboardNavigation);
        });

        this._$menuBar.addEventListener('keydown', this._handleMenuBarKeyboardNavigation);
    }

    private _handleSubMenuKeyboardNavigation = (ev: KeyboardEvent): void => {
        const $target = ev.target as HTMLAnchorElement | null;
        const $menuItem = ev.currentTarget as HTMLLIElement | null;

        if (!$target || !$menuItem?.classList.contains(MENU_CSS_CLASSES.opened)) return;

        const isContainedWithinMobileMenu = !$target.closest<HTMLUListElement>('[role="menubar"]');

        if (isContainedWithinMobileMenu) return;

        ev.stopPropagation();

        const isDirectMenuItem = $target.closest<HTMLLIElement>(`.${MENU_CSS_CLASSES.parent}`) === $menuItem && !$target.closest<HTMLElement>(`.${MENU_CSS_CLASSES.submenu}`);

        switch(ev.key) {
            case 'ArrowDown':
                ev.preventDefault();
                this._navigateDown($target);
                break;
            case 'ArrowUp':
                ev.preventDefault();
                this._navigateUp($target);
                break;
            case 'ArrowRight':
            case 'ArrowLeft':
                this._navigateArrows(ev);
                break;
            case 'Escape':
                this.navigationUtils.hideSubMenu($target);
                break;
            case 'Home':
                ev.preventDefault();

                isDirectMenuItem && this.navigationUtils.hideMenu($menuItem);

                this._navigateMenuFirst($target);
                break;
            case 'End':
                ev.preventDefault();

                isDirectMenuItem && this.navigationUtils.hideMenu($menuItem);

                this._navigateMenuLast($target);
                break;
            case 'Tab':
                this._handleTabNavigation(ev);
                break;
            default:
                this._focusItemByCharacter(ev);
                break;
        }
    }

    public _handleMenuBarKeyboardNavigation = (ev: KeyboardEvent): void => {
        const $target = ev.target as HTMLAnchorElement | null;

        if (!$target) return;

        const isContainedWithinMobileMenu = !$target.closest<HTMLUListElement>('[role="menubar"]');

        if (isContainedWithinMobileMenu) return;

        switch(ev.key) {
            case 'ArrowDown':
            case 'ArrowUp': {
                const hasPopup = $target.getAttribute('aria-haspopup') === 'true';

                if (!hasPopup) return;

                const $menuItem = $target.closest<HTMLLIElement>(`.${MENU_CSS_CLASSES.parent}`);

                if ($menuItem) {
                    ev.preventDefault();
                    this.navigationUtils.openMenu($menuItem);

                    const $currentlyFocusedLink = document.activeElement as HTMLAnchorElement | null;

                    if (ev.key === 'ArrowUp' && $currentlyFocusedLink) {
                        this._navigateMenuLast($currentlyFocusedLink);
                    }
                }
                break;
            }
            case 'ArrowLeft':
                this.navigationUtils.getPreviousTopLevelLink($target)?.focus();
                break;
            case 'ArrowRight':
                this.navigationUtils.getNextTopLevelLink($target)?.focus();
                break;
            case 'Home':
                ev.preventDefault();
                this.navigationUtils.$topLevelLinks[0]?.focus();
                break;
            case 'End':
                ev.preventDefault();
                this.navigationUtils.$topLevelLinks.at(-1)?.focus();
                break;
            case 'Tab':
                this._handleTabNavigation(ev);
                break;
            default:
                this._focusItemByCharacter(ev);
                break;
        }
    }

    private _handleTabNavigation(ev: KeyboardEvent): void {
        ev.preventDefault();

        const $menuItems = this.navigationUtils.$topLevelItemsWithSubmenu;

        $menuItems.forEach(($menuItem) => {
            if (!$menuItem.classList.contains(MENU_CSS_CLASSES.opened)) return;

            this.navigationUtils.hideMenu($menuItem);
        });

        if (ev.shiftKey) {
            UiDomUtils.getPreviousFocusableElement(UiDomUtils.getFocusableElement(this._$menuBar))?.focus();
            return;
        }

        UiDomUtils.getNextFocusableElement(UiDomUtils.getLastFocusableElement(this._$menuBar))?.focus();
    }

    private _navigateDown($menuLink: HTMLAnchorElement): void {
        const isSubmenuLink = !!$menuLink.closest<HTMLElement>(`.${MENU_CSS_CLASSES.submenu}`);

        if (isSubmenuLink) {
            const $allLinkSiblings = this.navigationUtils.getAllLinksInCurrentLevel($menuLink);
            const $nextLink = $allLinkSiblings[$allLinkSiblings.indexOf($menuLink) + 1] || $allLinkSiblings[0];
    
            $nextLink?.focus();
            return;
        }

        const $menuItem = $menuLink.closest<HTMLLIElement>(`.${MENU_CSS_CLASSES.parent}`);
        const $submenu = $menuItem?.querySelector<HTMLElement>(`.${MENU_CSS_CLASSES.submenu}`);

        $submenu && UiDomUtils.getFocusableElement($submenu)?.focus();
    }

    private _navigateUp($menuLink: HTMLAnchorElement): void {
        const isSubmenuLink = !!$menuLink.closest<HTMLElement>(`.${MENU_CSS_CLASSES.submenu}`);

        if (isSubmenuLink) {
            const $allLinkSiblings = this.navigationUtils.getAllLinksInCurrentLevel($menuLink);
            const $nextLink = $allLinkSiblings[$allLinkSiblings.indexOf($menuLink) - 1] || $allLinkSiblings.at(-1);

            $nextLink?.focus()
            return;
        }

        const $menuItem = $menuLink.closest<HTMLLIElement>(`.${MENU_CSS_CLASSES.parent}`);
        const $submenu = $menuItem?.querySelector<HTMLElement>(`.${MENU_CSS_CLASSES.submenu}`);

        if (!$submenu) return;

        const $directSubmenuLinks = this.navigationUtils.getDirectChildLinksFromContainer($submenu);

        $directSubmenuLinks.at(-1)?.focus();
    }

    private _navigateArrows(ev: KeyboardEvent): void {
        if (ev.key === 'ArrowLeft') {
            this._handleArrowNavigation(ev, { isRightArrow: false });
        } else if (ev.key === 'ArrowRight') {
            this._handleArrowNavigation(ev, { isRightArrow: true });
        }
    }

    private _handleArrowNavigation(ev: KeyboardEvent, { isRightArrow }: THandleArrowNavigationOptions): void {
        const $menuLink = ev.target as HTMLAnchorElement | null;

        if (!$menuLink) return;

        const $menuItem = $menuLink.closest<HTMLLIElement>(`.${MENU_CSS_CLASSES.parent}`);
        const $parentSubMenu = $menuLink.closest<HTMLElement>(`.${MENU_CSS_CLASSES.submenu}`);

        const isDirectMenuItem = !$parentSubMenu && !!$menuItem;
        const isFirstLevelSubMenuItem = !!$parentSubMenu?.classList.contains(MENU_CSS_CLASSES.level1);

        if (isRightArrow) {
            if (!$menuLink.hasAttribute('aria-haspopup') || isDirectMenuItem) {
                const $originMenuItem = this.navigationUtils.$topLevelItemsWithSubmenu.find(
                    ($menuItem) => $menuItem.contains($menuLink)
                );
                const $originMenuLink = $originMenuItem?.querySelector('a');
                const $originSubMenu = $originMenuItem?.querySelector<HTMLElement>(`.${MENU_CSS_CLASSES.submenu}`);

                if (!$originMenuItem || !$originMenuLink || !$originSubMenu) return;

                this._closeMenuAndNavigate({
                    $menuItem: $originMenuItem,
                    navigateCallback: ($originMenuLink) => this._navigateToNextMenuLink($originMenuLink)
                });

                return;
            }

            $menuItem && this.navigationUtils.openMenu($menuItem);
        } else {
            if (isFirstLevelSubMenuItem || isDirectMenuItem) {
                if (!$menuItem) return;

                this._closeMenuAndNavigate({
                    $menuItem: isFirstLevelSubMenuItem && $parentSubMenu?.closest<HTMLLIElement>(`.${MENU_CSS_CLASSES.parent}`) || $menuItem,
                    navigateCallback: ($menuLink) => this._navigateToPreviousMenuLink($menuLink)
                });
                return;
            }

            this.navigationUtils.hideSubMenu($menuLink);
        }
    }

    private _closeMenuAndNavigate({
        $menuItem,
        navigateCallback,
    }: TCloseAndNavigateOptions): void {
        const $menuItemToClose = $menuItem;
        const $menuLinkToClose = $menuItemToClose?.querySelector('a');

        if (!$menuLinkToClose) return;

        if ($menuItemToClose && $menuLinkToClose.getAttribute('aria-haspopup')) {
            this.navigationUtils.hideMenu($menuItemToClose);
        }

        navigateCallback($menuLinkToClose);
    }

    private _navigateToPreviousMenuLink($menuLink: HTMLAnchorElement): void {
        this.navigationUtils.openPreviousTopLevelMenu($menuLink);
        this.navigationUtils.getPreviousTopLevelLink($menuLink)?.focus();
    }

    private _navigateToNextMenuLink($menuLink: HTMLAnchorElement): void {        
        this.navigationUtils.openNextTopLevelMenu($menuLink);
        this.navigationUtils.getNextTopLevelLink($menuLink)?.focus();
    }

    private _navigateMenuFirst($menuLink: HTMLAnchorElement): void {
        const $allMenuLinksInCurrentLevel = this.navigationUtils.getAllLinksInCurrentLevel($menuLink);
        $allMenuLinksInCurrentLevel[0]?.focus();
    }

    private _navigateMenuLast($menuLink: HTMLAnchorElement): void {
        const $allMenuLinksInCurrentLevel = this.navigationUtils.getAllLinksInCurrentLevel($menuLink);
        $allMenuLinksInCurrentLevel.at(-1)?.focus();
    }

    private _focusItemByCharacter(ev: KeyboardEvent): void {
        const isAPrintableCharacter = ev.key.match(/\P{C}/u);

        if (ev.key.length !== 1 || !isAPrintableCharacter) return;

        const $currentMenuLink = ev.target as HTMLAnchorElement | null;

        if (!$currentMenuLink) return;

        const $allMenuLinks = this.navigationUtils.getAllLinksInCurrentLevel($currentMenuLink);

        const $matchedMenuLinks = $allMenuLinks.filter(($menuLink) =>
            $menuLink.innerText?.toLowerCase().startsWith(ev.key.toLowerCase())
        );

        const currentIndex = $matchedMenuLinks.indexOf($currentMenuLink);
        const $nextMenuLink = $matchedMenuLinks[(currentIndex + 1) % $matchedMenuLinks.length];

        if ($currentMenuLink.getAttribute('aria-haspopup')) {
            const $menuItemToClose = $currentMenuLink.closest<HTMLLIElement>(`.${MENU_CSS_CLASSES.parent}`);
            $menuItemToClose && this.navigationUtils.hideMenu($menuItemToClose);
        }

        $nextMenuLink?.focus();
    }
}